import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Event, ResolveEnd, Router } from '@angular/router';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { CookieService } from 'ngx-cookie-service';
import { filter } from 'rxjs/operators';
import { APP_CONFIGURATION_TOKEN, AppConfiguration } from './app-configuration';


/**
 * This service monitor errors and navigation pages with Application Insight.
 */
@Injectable({ providedIn: 'root' })
export class MonitoringService {

	appInsights: ApplicationInsights;
	hasFuncCookies: boolean = false;

	private readonly exceptionSeverityLevel = SeverityLevel.Warning;
	private readonly traceSeverityLevel = SeverityLevel.Information;

	consoleError = console.error;

	constructor(
		private readonly router: Router,
		private readonly cookieSrv: CookieService,
		@Inject(APP_CONFIGURATION_TOKEN) configs: AppConfiguration
	) {
		const funcCookie = cookieSrv.get(configs.cookieName);
		this.hasFuncCookies = funcCookie ? JSON.parse(funcCookie)?.purposes?.[2] : false;

		this.appInsights = new ApplicationInsights({
			config: {
				instrumentationKey: configs.insightKey,
				disableCookiesUsage: !this.hasFuncCookies,
				extensionConfig: {
					["AppInsightsCfgSyncPlugin"]: { cfgUrl: '' }
				}
			}
		});

		const consoleLog = console.log;
		const consoleWarn = console.warn;
		console.log = (msg, params) => {
			params ? consoleLog(msg, params) : consoleLog(msg);
			this.logEvent(msg, params);
		};
		console.warn = (msg, params) => {
			params ? consoleWarn(msg, params) : consoleWarn(msg);
			this.logEvent(msg, params);
		};
		console.error = (msg, params) => {
			this.logError(msg, params);
		}

		this.appInsights?.loadAppInsights();

		if (this.hasFuncCookies) {
			this.router.events.pipe(filter(event => event instanceof ResolveEnd)).subscribe((event: Event) => {
				if (event instanceof ResolveEnd) {
					const activatedComponent = this.getActivatedComponent(event.state.root);
					if (activatedComponent) {
						this.logPageView(`${activatedComponent.name} ${this.getRouteTemplate(event.state.root)}`, event.urlAfterRedirects);
					}
				}
			});
		}
	}

	/**
	* Track log events.
	*/
	logEvent(message: string, properties?: { [key: string]: string }): void {
		this.appInsights?.trackTrace({ message, severityLevel: this.traceSeverityLevel }, this.addGlobalProperties(properties));
	}

	private addGlobalProperties(properties?: { [key: string]: string }): { [key: string]: string } {
		if (!properties) properties = {};
		// add custom properties such as app version
		return properties;
	}

	/**
	* Track log errors.
	*/
	logError(error: any, params?: any): void {
		this.appInsights?.trackException({ error, severityLevel: this.exceptionSeverityLevel });
		params ? this.consoleError(error, params) : this.consoleError(error);
	}

	/**
	* Track current page.
	*/
	logPageView(name?: string, uri?: string): void {
		this.appInsights?.trackPageView({ name, uri });
	}

	private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
		if (snapshot.firstChild) {
			return this.getActivatedComponent(snapshot.firstChild);
		}
		return snapshot.component;
	}

	private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
		let path = '';
		if (snapshot.routeConfig) {
			path += snapshot.routeConfig.path;
		}
		if (snapshot.firstChild) {
			return path + this.getRouteTemplate(snapshot.firstChild);
		}
		return path;
	}

	/**
	* Track authenticated user.
	*/
	setAuthenticatedUserId(userId: string): void {
		if (this.hasFuncCookies) {
			this.appInsights?.setAuthenticatedUserContext(userId);
		}
	}

	/**
	* Reset authenticated user.
	*/
	clearAuthenticatedUserId(): void {
		this.appInsights?.clearAuthenticatedUserContext();
	}
}
